import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.role === "admin") navigate("/admin");
      else navigate("/portal");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4" data-testid="login-page">
      <div className="w-full max-w-sm">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#475569] hover:text-[#0F172A] mb-6 transition-colors" data-testid="login-back-home">
          <ArrowLeft className="w-4 h-4" /> Volver al inicio
        </Link>
        <div className="text-center mb-8">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4">
            <img src="https://customer-assets.emergentagent.com/job_promo-quote/artifacts/wgwlu5pd_WhatsApp%20Image%202026-03-27%20at%2018.43.52.jpeg" alt="PYM" className="h-10 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Chivo' }}>Iniciar Sesión</h1>
          <p className="text-sm text-[#475569] mt-1">Accede a tu cuenta PYM</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm flex items-start gap-2" data-testid="login-error">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-[#0F172A]">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="tu@email.com" className="mt-1 border-[#CBD5E1] rounded-sm focus:ring-2 focus:ring-[#0047AB]/20 focus:border-[#0047AB]" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-[#0F172A]">Contraseña</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Tu contraseña" className="mt-1 border-[#CBD5E1] rounded-sm focus:ring-2 focus:ring-[#0047AB]/20 focus:border-[#0047AB]" data-testid="login-password-input" />
              <Link to="/forgot-password" className="text-xs text-[#0047AB] hover:underline mt-1 inline-block" data-testid="forgot-password-link">¿Olvidaste tu contraseña?</Link>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#E30613] text-white font-bold rounded-sm py-2.5 hover:bg-[#C80410] transition-colors" data-testid="login-submit-btn">
              {loading ? "Ingresando..." : "Iniciar Sesión"}
            </Button>
          </form>
        </div>
        <p className="text-center text-sm text-[#475569] mt-4">
          ¿No tienes cuenta?{" "}
          <Link to="/registro" className="text-[#0047AB] font-medium hover:underline" data-testid="login-register-link">Regístrate aquí</Link>
        </p>
      </div>
    </div>
  );
}
